import React, { useState, useEffect, useRef } from "react";
import { Button } from "../ui/components/Button";
import { IconButton } from "../ui/components/IconButton";
import { TextField } from "../ui/components/TextField";
import { Tabs } from "../ui/components/Tabs";
import { DialogLayout } from "../ui/layouts/DialogLayout";
import {
  FeatherX,
  FeatherChevronDown,
  FeatherAlertTriangle,
} from "@subframe/core";
import {
  createBankAccount,
  updateBankAccount,
  deleteBankAccount,
  type AllAccount,
} from "../services/api";
import { usePermissions } from "../shared/presentation/hooks/usePermissions";
import { MODULE_CODES, ACTION_CODES } from "../shared/constants/permissions";
import { fetchCurrencies, Currency } from "../shared/data/api/currenciesApi";
import { ReviewRequestModal } from "../shared/presentation/components/ReviewRequestModal";
import { useToast } from "../shared/presentation/components";
import { SearchableDropdown } from "../shared/presentation/components";

export interface AddBankAccountFormData {
  account_name: string;
  account_type: "bank" | "credit";
  account_number: string;
  currency_id: string;
  currency_code: string;
  /** Optional; if set, opening_balance_date is required */
  opening_balance: string;
  /** YYYY-MM-DD; required when opening_balance is non-empty */
  opening_balance_date: string;
}

interface AddBankAccountModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
  /** Defaults to create */
  mode?: "create" | "edit";
  /** Required when mode is edit */
  editAccount?: AllAccount | null;
}

const emptyForm = (): AddBankAccountFormData => ({
  account_name: "",
  account_type: "bank",
  account_number: "",
  currency_id: "",
  currency_code: "",
  opening_balance: "",
  opening_balance_date: "",
});

/** Map API account_type to form tabs; credit_card and credit → credit, everything else → bank */
const toFormAccountType = (t: string | undefined | null): "bank" | "credit" => {
  const x = String(t ?? "")
    .toLowerCase()
    .trim()
    .replace(/\s+/g, "_");
  if (x === "credit" || x === "credit_card") {
    return "credit";
  }
  return "bank";
};

/** True if opening balance inputs are unchanged (allows 100 vs 100.00) */
const openingBalanceFieldsUnchanged = (
  currentBalance: string,
  currentDate: string,
  snapshot: { balance: string; date: string } | null,
): boolean => {
  if (!snapshot) return false;
  const a = currentBalance.trim();
  const b = snapshot.balance.trim();
  const d1 = currentDate.trim();
  const d2 = snapshot.date.trim();
  if (d1 !== d2) return false;
  if (a === b) return true;
  if (a === "" && b === "") return true;
  const na = Number(a);
  const nb = Number(b);
  if (
    a !== "" &&
    b !== "" &&
    !Number.isNaN(na) &&
    !Number.isNaN(nb) &&
    na === nb
  ) {
    return true;
  }
  return false;
};

const AddBankAccountModal: React.FC<AddBankAccountModalProps> = ({
  open,
  onOpenChange,
  onSuccess,
  mode = "create",
  editAccount = null,
}) => {
  const { getPermissionInfo, hasPermission } = usePermissions();
  const { showSuccess, showError } = useToast();

  const { requiresReview: requiresReviewCreate } = getPermissionInfo(
    MODULE_CODES.BANK_ACCOUNT,
    ACTION_CODES.CREATE,
  );
  const { requiresReview: requiresReviewUpdate } = getPermissionInfo(
    MODULE_CODES.BANK_ACCOUNT,
    ACTION_CODES.UPDATE,
  );
  const { requiresReview: requiresReviewDelete } = getPermissionInfo(
    MODULE_CODES.BANK_ACCOUNT,
    ACTION_CODES.DELETE,
  );
  const canUpdateBankAccount = hasPermission(
    MODULE_CODES.BANK_ACCOUNT,
    ACTION_CODES.UPDATE,
  );
  const canDeleteBankAccount = hasPermission(
    MODULE_CODES.BANK_ACCOUNT,
    ACTION_CODES.DELETE,
  );

  const [formData, setFormData] = useState<AddBankAccountFormData>(emptyForm);
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [currenciesLoading, setCurrenciesLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [isUpdateReviewModalOpen, setIsUpdateReviewModalOpen] = useState(false);
  const [isDeleteReviewModalOpen, setIsDeleteReviewModalOpen] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  /** Captured when edit modal opens — if submit matches this, omit OB from PUT */
  const editOpeningBalanceSnapshotRef = useRef<{
    balance: string;
    date: string;
  } | null>(null);

  useEffect(() => {
    if (!open) return;
    setErrors({});
    if (mode === "edit" && editAccount) {
      const ob = editAccount.opening_balance;
      const obNum =
        ob == null
          ? NaN
          : typeof ob === "number"
            ? ob
            : Number(String(ob).replace(/,/g, "").trim());
      const hasOb = !Number.isNaN(obNum) && obNum > 0;
      const rawDate = editAccount.opening_balance_date;
      const dateStr =
        typeof rawDate === "string" && rawDate.length >= 10
          ? rawDate.slice(0, 10)
          : "";
      const balanceStr = hasOb ? String(obNum) : "";
      editOpeningBalanceSnapshotRef.current = {
        balance: balanceStr,
        // Preserve date even if opening_balance can't be parsed (e.g. "1,000.00")
        date: dateStr,
      };
      setFormData({
        account_name: editAccount.bank_name ?? "",
        account_type: toFormAccountType(editAccount.account_type),
        account_number: editAccount.account_number?.trim() ?? "",
        currency_id: editAccount.currency_id ?? "",
        currency_code: editAccount.currency_code ?? "",
        opening_balance: balanceStr,
        opening_balance_date: dateStr,
      });
    } else if (mode === "create") {
      editOpeningBalanceSnapshotRef.current = null;
      setFormData(emptyForm());
    }
    if (!open) {
      setIsDeleteConfirmOpen(false);
    }
  }, [open, mode, editAccount]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setCurrenciesLoading(true);
    fetchCurrencies()
      .then((res) => {
        if (!cancelled && res.data) setCurrencies(res.data);
      })
      .catch(() => {
        if (!cancelled) setCurrencies([]);
      })
      .finally(() => {
        if (!cancelled) setCurrenciesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  const handleClose = () => {
    if (!isSubmitting) {
      setErrors({});
      onOpenChange(false);
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!formData.account_name?.trim()) {
      newErrors.account_name = "Account name is required";
    }
    if (!formData.account_number?.trim()) {
      newErrors.account_number = "Account number is required";
    }
    if (!formData.currency_id) {
      newErrors.currency_id = "Currency is required";
    }
    const obStr = formData.opening_balance?.trim() ?? "";
    const obDate = formData.opening_balance_date?.trim() ?? "";
    // Opening balance behavior:
    // - empty or 0 => treat as "no opening balance" (allowed) and date must be empty
    // - > 0 => date required
    if (obStr !== "") {
      const n = Number(obStr);
      if (Number.isNaN(n) || n < 0) {
        newErrors.opening_balance = "Enter a valid amount";
      } else if (n === 0) {
        if (obDate !== "") {
          newErrors.opening_balance_date =
            "Clear the date when opening balance is 0";
        }
      } else {
        if (!obDate) {
          newErrors.opening_balance_date =
            "Date is required when an opening balance is entered";
        }
      }
    }
    if (obDate !== "" && (obStr === "" || Number(obStr) === 0)) {
      newErrors.opening_balance =
        "Opening balance must be greater than 0 when a date is selected";
    }
    if (obDate !== "" && obStr !== "" && Number(obStr) > 0) {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(obDate)) {
        newErrors.opening_balance_date = "Use a valid date";
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const buildCreatePayload = () => {
    const base = {
      account_name: formData.account_name.trim(),
      account_type: formData.account_type,
      account_number: formData.account_number.trim(),
      currency_id: formData.currency_id,
      currency_code: formData.currency_code,
    };
    const obStr = formData.opening_balance?.trim() ?? "";
    if (obStr === "") return base;
    return {
      ...base,
      opening_balance: Number(obStr),
      opening_balance_date: formData.opening_balance_date.trim(),
    };
  };

  /**
   * Update: omit opening_balance fields when unchanged vs snapshot so the API
   * does not touch Zoho opening balances.
   */
  const buildUpdatePayload = () => {
    const base = {
      account_name: formData.account_name.trim(),
      account_type: formData.account_type,
      account_number: formData.account_number.trim(),
      currency_id: formData.currency_id,
      currency_code: formData.currency_code,
    };
    const obStr = formData.opening_balance?.trim() ?? "";
    const obDate = formData.opening_balance_date?.trim() ?? "";
    const snap = editOpeningBalanceSnapshotRef.current;
    if (
      openingBalanceFieldsUnchanged(
        formData.opening_balance ?? "",
        formData.opening_balance_date ?? "",
        snap,
      )
    ) {
      return base;
    }
    if (obStr === "") {
      return {
        ...base,
        opening_balance: 0,
        opening_balance_date: "",
      };
    }
    return {
      ...base,
      opening_balance: Number(obStr),
      opening_balance_date: obDate,
    };
  };

  const buildReviewRawJson = () => {
    const raw: Record<string, unknown> = {
      account_name: formData.account_name,
      account_type: formData.account_type,
      account_number: formData.account_number,
      currency_id: formData.currency_id,
      currency_code: formData.currency_code,
    };
    const obStr = formData.opening_balance?.trim() ?? "";
    if (obStr !== "") {
      raw.opening_balance = Number(obStr);
      raw.opening_balance_date = formData.opening_balance_date.trim();
    }
    return raw;
  };

  /** Review payload for edit: id + Zoho ids + same shape as PUT body */
  const buildReviewRawJsonForUpdate = () => {
    if (!editAccount) return {};
    return {
      id: editAccount.id,
      zoho_bankaccountid: editAccount.zoho_bankaccountid,
      opening_balance_id: editAccount.opening_balance_id ?? null,
      ...buildUpdatePayload(),
    };
  };

  const buildReviewOriginalJsonForUpdate = () => {
    if (!editAccount) return {};
    return {
      id: editAccount.id,
      zoho_bankaccountid: editAccount.zoho_bankaccountid,
      account_name: editAccount.bank_name ?? "",
      account_type:
        editAccount.account_type === "credit" ||
        editAccount.account_type === "credit_card"
          ? "credit"
          : "bank",
      account_number: editAccount.account_number?.trim() ?? "",
      currency_id: editAccount.currency_id ?? "",
      currency_code: editAccount.currency_code ?? "",
      opening_balance:
        editAccount.opening_balance != null
          ? Number(editAccount.opening_balance)
          : null,
      opening_balance_date: editAccount.opening_balance_date ?? null,
    };
  };

  const buildReviewRawJsonForDelete = () => {
    if (!editAccount) return {};
    return {
      id: editAccount.id,
      zoho_bankaccountid: editAccount.zoho_bankaccountid,
      opening_balance_id: editAccount.opening_balance_id ?? null,
      account_name: formData.account_name.trim(),
      account_type: formData.account_type,
      account_number: formData.account_number.trim(),
      currency_id: formData.currency_id,
      currency_code: formData.currency_code,
    };
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    if (mode === "create" && requiresReviewCreate) {
      setIsReviewModalOpen(true);
      return;
    }

    if (mode === "edit" && !editAccount) {
      showError("No account selected");
      return;
    }

    if (mode === "edit" && !canUpdateBankAccount) {
      showError("You do not have permission to update bank accounts");
      return;
    }

    if (mode === "edit" && requiresReviewUpdate) {
      setIsUpdateReviewModalOpen(true);
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    try {
      if (mode === "edit" && editAccount) {
        await updateBankAccount(editAccount.id, buildUpdatePayload());
        showSuccess(
          formData.account_type === "credit"
            ? "Credit card account updated successfully"
            : "Bank account updated successfully",
        );
      } else {
        await createBankAccount(buildCreatePayload());
        showSuccess("Bank account created successfully");
      }
      onSuccess?.();
      handleClose();
    } catch (err: any) {
      showError(err?.message || "Failed to save bank account");
      setErrors({
        submit: err?.message || "Failed to save bank account",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteClick = () => {
    if (!editAccount || mode !== "edit") return;
    setIsDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (!editAccount || mode !== "edit") return;
    if (!canDeleteBankAccount) {
      showError("You do not have permission to delete bank accounts");
      return;
    }
    if (requiresReviewDelete) {
      setIsDeleteConfirmOpen(false);
      setIsDeleteReviewModalOpen(true);
      return;
    }
    setIsDeleting(true);
    setErrors({});
    try {
      await deleteBankAccount(editAccount.id);
      setIsDeleteConfirmOpen(false);
      showSuccess(
        formData.account_type === "credit"
          ? "Credit card account deleted"
          : "Bank account deleted",
      );
      onSuccess?.();
      onOpenChange(false);
    } catch (err: any) {
      showError(err?.message || "Failed to delete bank account");
      setErrors({
        submit: err?.message || "Failed to delete bank account",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <DialogLayout open={open} onOpenChange={onOpenChange}>
        <div className="flex w-144 flex-col items-start gap-6 bg-white px-8 py-8">
          <div className="flex w-full items-center justify-between">
            <span className="text-heading-2 font-heading-2 text-default-font">
              {mode === "edit"
                ? formData.account_type === "credit"
                  ? "Edit credit card account"
                  : "Edit bank account"
                : "Add bank account"}
            </span>
            <IconButton
              icon={<FeatherX />}
              onClick={handleClose}
              disabled={isSubmitting || isDeleting}
            />
          </div>

          <div className="flex w-full flex-col items-start gap-4">
            {mode === "create" ? (
              <Tabs className="w-full">
                <Tabs.Item
                  active={formData.account_type === "bank"}
                  onClick={() =>
                    setFormData((prev) => ({ ...prev, account_type: "bank" }))
                  }
                >
                  Bank account
                </Tabs.Item>
                <Tabs.Item
                  active={formData.account_type === "credit"}
                  onClick={() =>
                    setFormData((prev) => ({
                      ...prev,
                      account_type: "credit",
                    }))
                  }
                >
                  Credit card
                </Tabs.Item>
              </Tabs>
            ) : (
              <div className="flex w-full flex-col gap-1 rounded-md border border-solid border-neutral-border bg-neutral-50 px-4 py-3">
                <span className="text-caption-bold font-caption-bold text-subtext-color">
                  Account type
                </span>
                <span className="text-body font-body text-default-font">
                  {formData.account_type === "credit"
                    ? "Credit card"
                    : "Bank account"}
                </span>
              </div>
            )}

            {errors.submit && (
              <div className="w-full rounded-md border border-error-200 bg-error-50 px-4 py-3">
                <span className="text-body font-body text-error-700">
                  {errors.submit}
                </span>
              </div>
            )}

            <TextField
              className="h-auto w-full flex-none"
              variant="filled"
              label="Account name"
              helpText="Display name for this account"
              error={!!errors.account_name}
            >
              <TextField.Input
                placeholder="e.g. HDFC Current Account"
                value={formData.account_name}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setFormData((prev) => ({
                    ...prev,
                    account_name: e.target.value,
                  }));
                  setErrors((prev) => ({ ...prev, account_name: "" }));
                }}
              />
            </TextField>

            <TextField
              className="h-auto w-full flex-none"
              variant="filled"
              label="Account number"
              helpText="Bank or card account number"
              error={!!errors.account_number}
            >
              <TextField.Input
                placeholder="Enter account number"
                value={formData.account_number}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setFormData((prev) => ({
                    ...prev,
                    account_number: e.target.value,
                  }));
                  setErrors((prev) => ({ ...prev, account_number: "" }));
                }}
              />
            </TextField>

            <div className="flex w-full flex-col gap-1">
              <span className="text-caption-bold font-caption-bold text-default-font">
                Currency *
              </span>
              <SearchableDropdown
                items={currencies}
                loading={currenciesLoading}
                trigger={
                  <Button
                    variant="neutral-secondary"
                    className="w-full justify-between"
                    iconRight={<FeatherChevronDown />}
                    disabled={currenciesLoading}
                  >
                    {currenciesLoading
                      ? "Loading currencies..."
                      : formData.currency_id
                        ? currencies.find(
                            (c) => c.currency_id === formData.currency_id,
                          )?.currency_code ||
                          currencies.find(
                            (c) => c.currency_id === formData.currency_id,
                          )?.currency_name ||
                          "Select currency"
                        : "Select currency"}
                  </Button>
                }
                getSearchableText={(c) =>
                  `${c.currency_code} – ${c.currency_name}`
                }
                renderItem={(c) => `${c.currency_code} – ${c.currency_name}`}
                getItemKey={(c) => c.currency_id}
                onSelect={(c) =>
                  setFormData((prev) => ({
                    ...prev,
                    currency_id: c.currency_id,
                    currency_code: c.currency_code,
                  }))
                }
                selectedKey={formData.currency_id}
                searchPlaceholder="Search currencies..."
                emptyMessage="No currencies available"
                noResultsMessage="No currencies match your search"
              />
              {errors.currency_id && (
                <span className="text-caption font-caption text-error-700">
                  {errors.currency_id}
                </span>
              )}
            </div>

            {/* <TextField
              className="h-auto w-full flex-none"
              variant="filled"
              label="Opening balance (optional)"
              helpText="If set, opening balance date is required"
              error={!!errors.opening_balance}
            >
              <TextField.Input
                type="number"
                step="0.01"
                min="0"
                placeholder="0.00"
                value={formData.opening_balance}
                onWheel={(e: React.WheelEvent<HTMLInputElement>) => {
                  // Prevent scroll-wheel from changing the numeric value while scrolling the modal
                  (e.currentTarget as HTMLInputElement).blur();
                }}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const next = e.target.value;
                  setFormData((prev) => ({
                    ...prev,
                    opening_balance: next,
                    // If user clears or sets 0, treat it as "no opening balance" and clear the date too
                    opening_balance_date:
                      next.trim() === "" || Number(next) === 0
                        ? ""
                        : prev.opening_balance_date,
                  }));
                  setErrors((prev) => ({
                    ...prev,
                    opening_balance: "",
                  }));
                }}
              />
            </TextField> */}

            {/* <TextField
              className="h-auto w-full flex-none"
              variant="filled"
              label="Opening balance date"
              helpText="Required when opening balance is entered"
              error={!!errors.opening_balance_date}
            >
              <TextField.Input
                type="date"
                value={formData.opening_balance_date}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setFormData((prev) => ({
                    ...prev,
                    opening_balance_date: e.target.value,
                  }));
                  setErrors((prev) => ({
                    ...prev,
                    opening_balance_date: "",
                  }));
                }}
              />
            </TextField> */}
          </div>

          <div className="flex w-full flex-wrap items-center justify-between gap-2 border-t border-solid border-neutral-border pt-6">
            <div className="flex items-center gap-2">
              {mode === "edit" && canDeleteBankAccount && (
                <Button
                  variant="destructive-secondary"
                  onClick={handleDeleteClick}
                  disabled={isSubmitting || isDeleting}
                >
                  Delete account
                </Button>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="neutral-tertiary"
                onClick={handleClose}
                disabled={isSubmitting || isDeleting}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={
                  isSubmitting ||
                  isDeleting ||
                  (mode === "edit" && !canUpdateBankAccount)
                }
                loading={isSubmitting}
              >
                {mode === "edit"
                  ? requiresReviewUpdate
                    ? "Submit for review"
                    : "Save changes"
                  : requiresReviewCreate
                    ? "Submit for review"
                    : "Create account"}
              </Button>
            </div>
          </div>
        </div>
      </DialogLayout>

      {isDeleteConfirmOpen && editAccount && mode === "edit" && (
        <DialogLayout
          open={isDeleteConfirmOpen}
          onOpenChange={(next) => {
            if (!next && !isDeleting) {
              setIsDeleteConfirmOpen(false);
            }
          }}
        >
          <div className="flex w-120 flex-col items-start gap-6 bg-white px-8 py-8">
            <div className="flex items-start gap-4">
              <div className="flex h-10 w-10 flex-none items-center justify-center rounded-full bg-error-100">
                <FeatherAlertTriangle className="text-error-600" />
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-heading-3 font-heading-3 text-default-font">
                  {formData.account_type === "credit"
                    ? "Delete credit card account?"
                    : "Delete bank account?"}
                </span>
                <span className="text-body font-body text-subtext-color">
                  This will permanently remove{" "}
                  <strong>{editAccount.bank_name}</strong> from Zoho Books and
                  cannot be undone.
                </span>
              </div>
            </div>
            <div className="flex w-full items-center justify-end gap-3 border-t border-solid border-neutral-border pt-4">
              <Button
                variant="neutral-tertiary"
                onClick={() => setIsDeleteConfirmOpen(false)}
                disabled={isDeleting}
              >
                Cancel
              </Button>
              <Button
                variant="destructive-primary"
                onClick={confirmDelete}
                loading={isDeleting}
                disabled={isDeleting}
              >
                Delete
              </Button>
            </div>
          </div>
        </DialogLayout>
      )}

      {mode === "create" && (
        <ReviewRequestModal
          open={isReviewModalOpen}
          onOpenChange={setIsReviewModalOpen}
          moduleCode="bank_account"
          actionCode="create"
          entityType="bank_account"
          entityId={0}
          changeDetails={{
            raw_json: buildReviewRawJson(),
          }}
          skipNavigation={true}
          onSuccess={() => {
            setIsReviewModalOpen(false);
            onSuccess?.();
            onOpenChange(false);
          }}
        />
      )}
      {mode === "edit" && editAccount && (
        <>
          <ReviewRequestModal
            open={isUpdateReviewModalOpen}
            onOpenChange={setIsUpdateReviewModalOpen}
            moduleCode="bank_account"
            actionCode="update"
            entityType="bank_account"
            entityId={editAccount.id}
            changeDetails={{
              raw_json: buildReviewRawJsonForUpdate(),
              original_json: buildReviewOriginalJsonForUpdate(),
            }}
            skipNavigation={true}
            onSuccess={() => {
              setIsUpdateReviewModalOpen(false);
              onSuccess?.();
              onOpenChange(false);
            }}
          />
          <ReviewRequestModal
            open={isDeleteReviewModalOpen}
            onOpenChange={setIsDeleteReviewModalOpen}
            moduleCode="bank_account"
            actionCode="delete"
            entityType="bank_account"
            entityId={editAccount.id}
            changeDetails={{
              raw_json: buildReviewRawJsonForDelete(),
            }}
            skipNavigation={true}
            onSuccess={() => {
              setIsDeleteReviewModalOpen(false);
              onSuccess?.();
              onOpenChange(false);
            }}
          />
        </>
      )}
    </>
  );
};

export default AddBankAccountModal;
